import { useState } from "react";
import { Check, Pencil, X } from "lucide-react";
import { api } from "../lib/api";

/** W033 Q17: inline "Not quite" correction control for an inferred,
 *  extractor-derived field. Submitting records the correction via
 *  POST /api/extractor/correct so that extractor's next LLM call for
 *  this user is steered away from the mistake (few-shot reinforcement).
 *
 *  Compact by design: a single "Not quite" button expands to a text
 *  field pre-filled with the current value; Save posts the correction
 *  and collapses to a quiet acknowledgement. No em dashes in copy. */
export function ExtractorCorrect({
  extractorKey,
  field,
  currentValue,
  packetId,
  context,
  label = "Not quite",
}: {
  extractorKey: string;
  field: string;
  currentValue: string | null;
  packetId?: string | null;
  context?: Record<string, unknown>;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(currentValue ?? "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (done) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400">
        <Check className="w-3 h-3" /> Thanks. Memso will remember that.
      </span>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[11px] font-semibold px-1.5 py-1 hover:bg-slate-200 dark:hover:bg-slate-700"
        title={`Correct ${field}`}
      >
        <Pencil className="w-3 h-3" /> {label}
      </button>
    );
  }

  const save = async () => {
    const corrected = value.trim();
    if (!corrected || busy) return;
    setBusy(true);
    try {
      await api.extractorCorrect({
        extractor_key: extractorKey,
        field,
        original_value: currentValue,
        corrected_value: corrected,
        packet_id: packetId ?? null,
        context,
      });
      setDone(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex items-center gap-1">
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoFocus
        placeholder={`Correct ${field}`}
        className="w-36 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-1.5 py-1 text-[11px] text-slate-800 dark:text-slate-100"
        onKeyDown={(e) => {
          if (e.key === "Enter") void save();
          if (e.key === "Escape") setOpen(false);
        }}
      />
      <button
        type="button"
        onClick={() => void save()}
        disabled={busy || !value.trim()}
        className="inline-flex items-center rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold px-1.5 py-1 disabled:opacity-50"
        title="Save correction"
      >
        <Check className="w-3 h-3" />
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="inline-flex items-center rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500 px-1.5 py-1 hover:bg-slate-200 dark:hover:bg-slate-700"
        title="Cancel"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}
